/*
 * PersonalDictRoundtrip — String-Liste ⇄ Paragraph-Liste fuer den
 * Personal-Dictionary-Editor (Spec §10 Phase 4).
 *
 * Datenmodell: Eine Zeile = ein Wort. Die Server-Validierung erwartet jedes
 * Wort als 1..64-Zeichen-String (kein Leerraum), max 1000 Eintraege.
 *
 * Konvention:
 *   - paragraphsToWords: trim, leere/whitespace-only Zeilen wegwerfen,
 *     Mehrwort-Eintraege wegwerfen, Duplikate entfernen, locale-aware
 *     case-insensitive sortieren (de-CH = generisch europaeisch). Das
 *     Ergebnis ist die kanonische Form, die der Server speichert.
 *   - wordsToParagraphs: jeder String wird ein Paragraph (kein Markdown).
 *     Bei leerer Liste liefern wir genau einen leeren Paragraph zurueck —
 *     der ProseEditor muss mindestens einen Paragraph haben.
 *   - validateWord: pure Helper fuer Editor-UI (z.B. visuelle Markierung
 *     ungueltiger Zeilen). Liefert null fuer "ok" oder einen Code-String.
 *
 * Roundtrip-Idempotenz: paragraphsToWords(wordsToParagraphs(words))
 * liefert die gleiche Liste, sofern words bereits kanonisch ist
 * (sortiert, dedup, getrimmt, kein Whitespace).
 */

const PERSONAL_WORD_MAX = 64
const PERSONAL_LIST_MAX = 1000

/**
 * Wandelt eine Wort-Liste in ProseEditor-Paragraph-Daten.
 *
 * @param {string[]} words
 * @returns {Array<object>}
 */
export function wordsToParagraphs(words) {
    const list = Array.isArray(words) ? words : []
    if (list.length === 0) {
        return [emptyParagraph()]
    }
    return list.map(w => ({
        text: typeof w === 'string' ? w : '',
        outlineDepth: 0
    }))
}

/**
 * Liest die aktuellen ProseEditor-Paragraphen und destilliert die
 * kanonische Wort-Liste (Trim, Filter, Dedup, Sort).
 *
 * @param {Array<object>} paragraphs - serialisierte Paragraph-Daten oder
 *   Paragraph-Instanzen (es wird nur `.text` gelesen)
 * @returns {string[]}
 */
export function paragraphsToWords(paragraphs) {
    if (!Array.isArray(paragraphs)) return []
    const seen = new Set()
    const out = []
    for (const p of paragraphs) {
        const raw = (p && typeof p.text === 'string') ? p.text : ''
        const trimmed = raw.trim()
        if (trimmed === '') continue
        if (validateWord(trimmed) !== null) continue
        if (seen.has(trimmed)) continue
        seen.add(trimmed)
        out.push(trimmed)
    }
    out.sort((a, b) => a.localeCompare(b))
    return out
}

/**
 * Prueft ein einzelnes Wort gegen die Server-Constraints.
 * @param {string} word
 * @returns {null | 'empty' | 'too_long' | 'multi_word'}
 */
export function validateWord(word) {
    if (typeof word !== 'string') return 'empty'
    const trimmed = word.trim()
    if (trimmed.length === 0) return 'empty'
    if (trimmed.length > PERSONAL_WORD_MAX) return 'too_long'
    // Whitespace mitten im Wort → kein gueltiger Hunspell-Eintrag (die Engine
    // prueft Wort fuer Wort). Tabs, Newlines etc. werden vom Trim am Rand
    // erfasst, aber innen muss das Wort frei davon sein.
    if (/\s/.test(trimmed)) return 'multi_word'
    return null
}

function emptyParagraph() {
    return {
        text: '',
        outlineDepth: 0
    }
}

export const _internal = { PERSONAL_WORD_MAX, PERSONAL_LIST_MAX }
